/**
 * Design System Theme Configuration
 * Based on design.md specifications
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';

// ============================================================================
// Design Tokens
// ============================================================================

// Dark Theme Colors
export const darkColors = {
  // Primary Colors
  primary: {
    main: '#3B82F6',        // Lighter blue
    light: '#60A5FA',
    dark: '#2563EB',
    subtle: '#DBEAFE',
  },
  
  // Semantic Colors
  success: {
    main: '#10B981',
    light: '#D1FAE5',
  },
  warning: {
    main: '#F59E0B',
    light: '#FEF3C7',
  },
  error: {
    main: '#EF4444',
    light: '#FEE2E2',
  },
  info: {
    main: '#06B6D4',
    light: '#CFFAFE',
  },
  
  // Neutral Colors
  background: {
    default: '#0F172A',
    paper: '#1E293B',
    elevated: '#334155',
  },
  divider: '#334155',
  text: {
    primary: '#F1F5F9',
    secondary: '#94A3B8',
    disabled: '#64748B',
  },
  
  // Special Purpose
  tag: {
    selected: '#3B82F6',
    unselected: '#64748B',
    hover: '#60A5FA',
  },
  chart: {
    primary: '#3B82F6',
    secondary: '#8B5CF6',
    tertiary: '#06B6D4',
  },
};

// Light Theme Colors (COSMOS-inspired)
export const lightColors = {
  // Primary Colors
  primary: {
    main: '#23227e',
    light: '#2E3A7D',
    dark: '#0F1442',
    subtle: '#E8EAFF',
  },
  
  // Semantic Colors
  success: {
    main: '#10B981',
    light: '#D1FAE5',
  },
  warning: {
    main: '#F59E0B',
    light: '#FEF3C7',
  },
  error: {
    main: '#EF4444',
    light: '#FEE2E2',
  },
  info: {
    main: '#06B6D4',
    light: '#CFFAFE',
  },
  
  // Neutral Colors
  background: {
    default: '#FFFFFF',
    paper: '#F8F9FA',
    elevated: '#FFFFFF',
  },
  divider: '#E5E7EB',
  text: {
    primary: '#23227e',
    secondary: '#6B7280',
    disabled: '#9CA3AF',
  },
  
  // Special Purpose
  tag: {
    selected: '#23227e',
    unselected: '#6B7280',
    hover: '#2E3A7D',
  },
  chart: {
    primary: '#23227e',
    secondary: '#8B5CF6',
    tertiary: '#06B6D4',
  },
};

// Export colors (defaults to dark for backward compatibility)
export const colors = darkColors;

export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  '2xl': 48,
  '3xl': 64,
};

export const typography = {
  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  fontMono: '"Fira Code", "Consolas", "Monaco", monospace',
};

export const borderRadius = {
  sm: 4,
  md: 6,
  lg: 8,
  xl: 12,
  pill: 16,
};

// ============================================================================
// MUI Theme Configuration
// ============================================================================

export const createAppTheme = (mode: 'light' | 'dark') => {
  const themeColors = mode === 'dark' ? darkColors : lightColors;
  
  const themeOptions: ThemeOptions = {
    palette: {
      mode,
      primary: {
        main: themeColors.primary.main,
        light: themeColors.primary.light,
        dark: themeColors.primary.dark,
      },
      secondary: {
        main: themeColors.primary.main,
      },
      success: {
        main: themeColors.success.main,
        light: themeColors.success.light,
      },
      warning: {
        main: themeColors.warning.main,
        light: themeColors.warning.light,
      },
      error: {
        main: themeColors.error.main,
        light: themeColors.error.light,
      },
      info: {
        main: themeColors.info.main,
        light: themeColors.info.light,
      },
      background: {
        default: themeColors.background.default,
        paper: themeColors.background.paper,
      },
      text: {
        primary: themeColors.text.primary,
        secondary: themeColors.text.secondary,
        disabled: themeColors.text.disabled,
      },
      divider: themeColors.divider,
    },
  
    typography: {
      fontFamily: typography.fontFamily,
    // Headings
    h1: {
      fontSize: '2rem',        // 32px
      lineHeight: 1.25,        // 40px
      fontWeight: 700,
      '@media (max-width:600px)': {
        fontSize: '1.75rem',   // 28px
      },
    },
    h2: {
      fontSize: '1.5rem',      // 24px
      lineHeight: 1.33,        // 32px
      fontWeight: 600,
      '@media (max-width:600px)': {
        fontSize: '1.25rem',   // 20px
      },
    },
    h3: {
      fontSize: '1.25rem',     // 20px
      lineHeight: 1.4,         // 28px
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.125rem',    // 18px
      lineHeight: 1.44,        // 26px
      fontWeight: 600,
    },
    h5: {
      fontSize: '1rem',        // 16px
      lineHeight: 1.5,         // 24px
      fontWeight: 600,
    },
    h6: {
      fontSize: '0.875rem',    // 14px
      lineHeight: 1.43,        // 20px
      fontWeight: 600,
    },
    // Body Text
    body1: {
      fontSize: '1rem',        // 16px
      lineHeight: 1.5,         // 24px
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.875rem',    // 14px
      lineHeight: 1.43,        // 20px
      fontWeight: 400,
    },
    caption: {
      fontSize: '0.75rem',     // 12px
      lineHeight: 1.5,         // 18px
      fontWeight: 400,
    },
    button: {
      fontSize: '0.875rem',    // 14px
      fontWeight: 600,
      textTransform: 'none',   // Don't uppercase buttons
    },
  },
  
  spacing: 8, // Base unit of 8px
  
  shape: {
    borderRadius: borderRadius.lg,
  },
  
  breakpoints: {
    values: {
      xs: 480,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
    },
  },
  
  components: {
    // ========================================================================
    // Button Components
    // ========================================================================
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.md,
          padding: '8px 16px',
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          '&:hover': {
            backgroundColor: themeColors.primary.light,
          },
        },
        outlined: {
          borderColor: themeColors.divider,
          '&:hover': {
            backgroundColor: themeColors.background.elevated,
            borderColor: themeColors.divider,
          },
        },
        text: {
          '&:hover': {
            backgroundColor: themeColors.background.elevated,
          },
        },
      },
    },
    
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.md,
          '&:hover': {
            backgroundColor: themeColors.background.elevated,
          },
        },
      },
    },
    
    // ========================================================================
    // Card & Paper Components
    // ========================================================================
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.lg,
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
        },
        elevation2: {
          boxShadow: '0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23)',
        },
        elevation3: {
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.lg,
          padding: spacing.lg,
        },
      },
    },
    
    // ========================================================================
    // Form Components
    // ========================================================================
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: borderRadius.md,
            backgroundColor: themeColors.background.elevated,
            '& fieldset': {
              borderColor: themeColors.divider,
            },
            '&:hover fieldset': {
              borderColor: themeColors.divider,
            },
            '&.Mui-focused fieldset': {
              borderColor: themeColors.primary.main,
              borderWidth: '2px',
            },
          },
        },
      },
    },
    
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.md,
          backgroundColor: themeColors.background.elevated,
          '& fieldset': {
            borderColor: themeColors.divider,
          },
          '&:hover fieldset': {
            borderColor: themeColors.divider,
          },
          '&.Mui-focused fieldset': {
            borderColor: themeColors.primary.main,
            borderWidth: '2px',
          },
        },
      },
    },
    
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.md,
        },
      },
    },
    
    // ========================================================================
    // Table Components
    // ========================================================================
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: themeColors.background.elevated,
          '& .MuiTableCell-root': {
            fontWeight: 600,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: themeColors.text.secondary,
            borderBottom: `2px solid ${themeColors.divider}`,
          },
        },
      },
    },
    
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            '&:hover': {
              backgroundColor: themeColors.background.elevated,
              cursor: 'pointer',
            },
          },
          '& .MuiTableCell-root': {
            borderBottom: `1px solid ${themeColors.divider}`,
          },
        },
      },
    },
    
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
        },
      },
    },
    
    // ========================================================================
    // Chip/Badge Components
    // ========================================================================
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.pill,
          fontWeight: 500,
          fontSize: '0.75rem',
        },
        filled: {
          backgroundColor: themeColors.background.elevated,
        },
      },
    },
    
    // ========================================================================
    // Tab Components
    // ========================================================================
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: `2px solid ${themeColors.divider}`,
          minHeight: 48,
        },
        indicator: {
          height: 2,
          backgroundColor: themeColors.primary.main,
        },
      },
    },
    
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          minHeight: 48,
          fontWeight: 500,
          fontSize: '0.875rem',
          color: themeColors.text.secondary,
          '&:hover': {
            backgroundColor: themeColors.background.elevated,
          },
          '&.Mui-selected': {
            color: themeColors.primary.main,
          },
        },
      },
    },
    
    // ========================================================================
    // Dialog/Modal Components
    // ========================================================================
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: borderRadius.xl,
          padding: spacing.xl,
          backgroundColor: themeColors.background.elevated,
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: `0 0 ${spacing.lg}px 0`,
          fontSize: '1.25rem',
          fontWeight: 600,
          borderBottom: `1px solid ${themeColors.divider}`,
        },
      },
    },
    
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: `${spacing.lg}px 0`,
        },
      },
    },
    
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: `${spacing.lg}px 0 0 0`,
          borderTop: `1px solid ${themeColors.divider}`,
          gap: spacing.sm,
        },
      },
    },
    
    // ========================================================================
    // Alert/Notification Components
    // ========================================================================
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.lg,
          padding: '12px 16px',
        },
        standardSuccess: {
          backgroundColor: themeColors.success.light,
          color: themeColors.success.main,
          borderLeft: `4px solid ${themeColors.success.main}`,
        },
        standardError: {
          backgroundColor: themeColors.error.light,
          color: themeColors.error.main,
          borderLeft: `4px solid ${themeColors.error.main}`,
        },
        standardWarning: {
          backgroundColor: themeColors.warning.light,
          color: themeColors.warning.main,
          borderLeft: `4px solid ${themeColors.warning.main}`,
        },
        standardInfo: {
          backgroundColor: themeColors.info.light,
          color: themeColors.info.main,
          borderLeft: `4px solid ${themeColors.info.main}`,
        },
      },
    },
    
    // ========================================================================
    // Menu/Dropdown Components
    // ========================================================================
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: borderRadius.lg,
          marginTop: spacing.sm,
          backgroundColor: themeColors.background.elevated,
        },
      },
    },
    
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
          '&:hover': {
            backgroundColor: themeColors.background.paper,
          },
          '&.Mui-selected': {
            backgroundColor: themeColors.background.paper,
            '&:hover': {
              backgroundColor: themeColors.background.paper,
            },
          },
        },
      },
    },
    
    // ========================================================================
    // Tooltip Components
    // ========================================================================
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: themeColors.background.elevated,
          color: themeColors.text.primary,
          fontSize: '0.75rem',
          padding: '8px 12px',
          borderRadius: borderRadius.md,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        },
      },
    },
    
    // ========================================================================
    // Progress Components
    // ========================================================================
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 8,
          borderRadius: borderRadius.sm,
          backgroundColor: themeColors.background.elevated,
        },
        bar: {
          borderRadius: borderRadius.sm,
          backgroundColor: themeColors.primary.main,
        },
      },
    },
    
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: themeColors.primary.main,
        },
      },
    },
    
    // ========================================================================
    // Checkbox/Radio Components
    // ========================================================================
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: themeColors.primary.main,
          },
        },
      },
    },
    
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: themeColors.primary.main,
          },
        },
      },
    },
    
      // ========================================================================
      // Divider Component
      // ========================================================================
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: themeColors.divider,
          },
        },
      },

    // ========================================================================
    // AppBar Component
    // ========================================================================
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
  },
};

return createTheme(themeOptions);
};

export default createAppTheme;
